import { ChangeEvent, Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import Select from 'react-select';
import { toast } from 'react-toastify';
import whitePaperPlane from '../../../../assets/images/icons/white-paper-plane.svg';
import NoData from '../../../../components/NoData';
import useThemeContext from '../../../../contexts/theme';
import useApiCall from '../../../../hooks/useApiCall';
import useGetGeneralAppData from '../../../../hooks/useGetGeneralAppData';
import ReclamationsService from '../../../../services/ReclamationsService';
import removeDuplicates from '../../../../utils/removeDuplicates';
import Button from '../../../Button';
import { CustomStyle } from '../../../CustomSelectStyle';
import { CustomStyle as CustomStyleDarkTheme } from '../../../CustomSelectStyleDarkTheme';
import FormGroup from '../../../FormGroup';
import MyModal from '../../../Modal';
import Textarea from '../../../Textarea';
import { HistoricoReclama, IAnswerStatusOptions, IAnswerTypesOptions, ISelectedValue } from '../../types';
import Reclamation from './components/Reclamation';
import { AddCommentContainer, AiHistoryButton, AiHistoryButtonWrapper, AiHistoryPopover, AiMessageBubble, CommentsContainer, ModalContainer, Section, SectionHeader, SelectsContainer } from './styles';

interface ICommentsModal {
  reclamationsModalShow: boolean;
  reclamations: HistoricoReclama[];
  aiHistoryChat?: string;
  handleCloseReclamationsModal: () => void;
  setIsLoading: Dispatch<SetStateAction<boolean>>,
  answerTypesOptions: IAnswerTypesOptions[],
  answerStatusOptions: IAnswerStatusOptions[],
  loadResult: () => Promise<void>,
  canEnableActions: boolean,
  companyCode: string,
  employeeMail: string,
  maxReclamations: number,
}

export default function ReclamationsModal({
  reclamationsModalShow,
  reclamations,
  handleCloseReclamationsModal,
  setIsLoading,
  answerTypesOptions,
  answerStatusOptions,
  loadResult,
  canEnableActions,
  companyCode,
  employeeMail,
  maxReclamations,
  aiHistoryChat,
}: ICommentsModal) {
  const [answerText, setAnswerText] = useState('');
  const [selectedAnswerStatus, setSelectedAnswerStatus] = useState({} as ISelectedValue);
  const [selectedAnswerType, setSelectedAnswerType] = useState({} as ISelectedValue);
  const [isAiHistoryOpen, setIsAiHistoryOpen] = useState(false);

  const { isCustomer } = useGetGeneralAppData();

  const commentsContainerRef = useRef<HTMLDivElement>(null);
  const aiHistoryRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    if (commentsContainerRef.current !== null) {
      commentsContainerRef.current.scrollTo({
        top: commentsContainerRef.current.scrollHeight,
        behavior: 'smooth', // Esta linha é opcional, adiciona uma animação suave
      });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [reclamationsModalShow]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (aiHistoryRef.current && !aiHistoryRef.current.contains(event.target as Node)) {
        setIsAiHistoryOpen(false);
      }
    }

    if (isAiHistoryOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isAiHistoryOpen]);

  const isMissingAnswer = !!reclamations && !(reclamations[(reclamations.length) - 1]?.resposta);

  const parsedAiHistory = useMemo(() => {
    if (!aiHistoryChat) return [];

    const messages: { role: 'user' | 'model'; content: string }[] = [];
    const parts = aiHistoryChat.split(/\n\n(?=user:|model:)/);

    parts.forEach((part) => {
      if (part.startsWith('user:')) {
        messages.push({
          role: 'user',
          content: part.replace(/^user:/, '').trim(),
        });
      } else if (part.startsWith('model:')) {
        messages.push({
          role: 'model',
          content: part.replace(/^model:/, '').trim(),
        });
      }
    });

    return messages;
  }, [aiHistoryChat]);

  const mappedAnswerStatusOptions = useMemo(() => (answerStatusOptions.map((x, index) => ({
    value: x.codStaResposta,
    label: `${index + 1}) ${x.tituloStatus}`,
  }))), [answerStatusOptions]);

  const mappedAnswerTypesOptions = useMemo(() => {
    const optionsFilteredBySelectedStatus = answerTypesOptions.filter((x) => x.codStaResposta === selectedAnswerStatus.value);

    const availableOptions = optionsFilteredBySelectedStatus.map((x, index) => ({
      value: x.codTipoResposta,
      label: `${index + 1}) ${x.categoriaTitulo}`,
    }));

    return availableOptions;
  }, [answerTypesOptions, selectedAnswerStatus.value]);

  function handleCommentTextChange(event: ChangeEvent<HTMLTextAreaElement>) {
    setAnswerText(event.target.value);
  }

  const { apiCall } = useApiCall();
  const { userCode, userProfile, userName, permissions } = useGetGeneralAppData();

  const { theme, selectedTheme } = useThemeContext();
  const { consultCode } = useParams();
  const reclamationsPermissions = permissions?.find((x) => x.codPag === 8);

  const selectStyles = useMemo(() => {
    const base = selectedTheme === 'dark' ? CustomStyleDarkTheme : CustomStyle;
    const borderColor = theme.colors.defaultBorder ?? theme.colors.gray[75];

    return {
      ...base,
      menuPortal: (provided: Record<string, unknown>) => ({
        ...base.menuPortal(provided),
        zIndex: 9999,
      }),
      menu: (provided: Record<string, unknown>) => ({
        ...base.menu(provided),
        backgroundColor: theme.colors.background,
        border: `1px solid ${borderColor}`,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      }),
      menuList: (provided: Record<string, unknown>) => ({
        ...base.menuList(provided),
        backgroundColor: theme.colors.background,
        color: theme.colors.gray[900],
      }),
      option: (provided: Record<string, unknown>, state: { isSelected: boolean; isFocused: boolean }) => ({
        ...base.option(provided, state),
        backgroundColor: state.isSelected
          ? theme.colors.primary.lighter
          : state.isFocused
            ? theme.colors.darkerBackground
            : theme.colors.background,
        color: theme.colors.primary.main,
        cursor: 'pointer',
      }),
    };
  }, [selectedTheme, theme]);

  function handleSelectedAnswerStatus(x: ISelectedValue) {
    setSelectedAnswerStatus(x);
    setSelectedAnswerType({} as ISelectedValue);
    setAnswerText('');
  }

  function handleSelectedAnswerType(x: ISelectedValue) {
    setSelectedAnswerType(x);
    const selectedOption = answerTypesOptions.find((opt) => opt.codTipoResposta === x.value);
    setAnswerText(selectedOption?.resposta || '');
  }

  const addAnswerOrReclamation = useCallback(async () => {
    const reclamationsWithoutAnswer = reclamations?.filter((x) => !x.resposta);

    await apiCall({
      apiToCall: isCustomer ? ReclamationsService.addReclamation : ReclamationsService.answerReclamation,
      reqBody: isCustomer ? JSON.stringify({
        codUsu: userCode,
        perfilUsu: userProfile,
        codEmpresa: companyCode,
        codConsulta: consultCode,
        codProblema: 200,
        mail: employeeMail,
        comentario: answerText,
      }) : JSON.stringify({
        codUsu: userCode,
        perfilUsu: userProfile,
        usuario: userName,
        listaResposta: removeDuplicates([{
          codTipoResposta: selectedAnswerType.value,
          codStaResposta: selectedAnswerStatus.value ?? 0,
          tituloStatus: selectedAnswerStatus.label?.slice(0, 299) || '',
          resposta: answerText,
          consulta: consultCode,
          codReclamacao: reclamations ? reclamations[(reclamations.length) - 1].codReclamacao : 0,
        },
        ...(reclamationsWithoutAnswer.length > 0 ? reclamationsWithoutAnswer.map((x) => ({
          codTipoResposta: selectedAnswerType.value,
          codStaResposta: selectedAnswerStatus.value ?? 0,
          tituloStatus: selectedAnswerStatus.label?.slice(0, 299) || '',
          resposta: answerText,
          consulta: consultCode,
          codReclamacao: reclamations ? x.codReclamacao : 0,
        })) : [])
        ]),
      }),
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      actionAfterResponse: async (response) => {
        if (!response.codigo) {
          toast.error(`Não foi possível registrar a ${isCustomer ? 'contestação' : 'resposta'}. Por favor, tente novamente. (${response.msg})`);
          return;
        }

        if (!isCustomer && !response.emailEnvio) {
          toast.warn(`O e-mail com a ${isCustomer ? 'contestação' : 'resposta'} não foi enviado ao usuário`);
        }


        if (!isCustomer) {
          await fetch(`${process.env.REACT_APP_NEST_API}/whats-reclamations/answered-reclamation`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              routingId: consultCode,
              answer: answerText,
            }),
          });
        }

        toast.success(`A ${isCustomer ? 'contestação' : 'resposta'} foi registrada com sucesso!`);
        handleCloseReclamationsModal();
        loadResult();
      }
    });
  }, [answerText, apiCall, companyCode, consultCode, employeeMail, handleCloseReclamationsModal, isCustomer, loadResult, reclamations, selectedAnswerStatus.label, selectedAnswerStatus.value, selectedAnswerType.value, setIsLoading, userCode, userName, userProfile]);

  return (
    <MyModal
      show={reclamationsModalShow}
      onClose={() => handleCloseReclamationsModal()}
      title={`Histórico de contestações e respostas (${reclamations?.length || 0}/${maxReclamations})`}
      minHeight='400px'
      modalBody={(
        <ModalContainer>
          <Section>
            <SectionHeader>
              <div className="section-title">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
                Histórico de Conversas
              </div>
              {!isCustomer && parsedAiHistory.length > 0 && (
                <AiHistoryButtonWrapper ref={aiHistoryRef}>
                  <AiHistoryButton onClick={() => setIsAiHistoryOpen(!isAiHistoryOpen)}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm0 18a8 8 0 1 1 8-8 8 8 0 0 1-8 8zm1-13h-2v6h2zm0 8h-2v2h2z" />
                    </svg>
                    Ver conversa com Lia
                  </AiHistoryButton>

                  <AiHistoryPopover isOpen={isAiHistoryOpen}>
                    <div className="popover-header">
                      <span>Conversa com Lia</span>
                      <button onClick={() => setIsAiHistoryOpen(false)}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <div className="popover-content">
                      {parsedAiHistory.map((message, index) => (
                        <AiMessageBubble key={index} isUser={message.role === 'user'}>
                          <span className="message-label">
                            {message.role === 'user' ? 'Usuário' : 'Lia'}
                          </span>
                          <div className="message-content">
                            {message.content}
                          </div>
                        </AiMessageBubble>
                      ))}
                    </div>
                  </AiHistoryPopover>
                </AiHistoryButtonWrapper>
              )}
            </SectionHeader>

            <CommentsContainer ref={commentsContainerRef} isMissingAnswer={isMissingAnswer}>
              {reclamations?.length > 0 ? (
                <>
                  {reclamations.map((rcl) => (
                    <Reclamation key={rcl.codReclamacao} reclamationObj={rcl} />
                  ))}
                </>
              ) : (
                <NoData
                  icon='emptyBox'
                  label="Nenhuma contestação foi realizada!"
                />
              )}
            </CommentsContainer>
          </Section>

          {(isMissingAnswer && !isCustomer && reclamationsPermissions?.incluir) && (
            <Section>
              <SectionHeader>
                <div className="section-title">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                  </svg>
                  Responder Contestação
                </div>
              </SectionHeader>
              <AddCommentContainer>
                <SelectsContainer>
                  <FormGroup aside={true}>
                    <label htmlFor="contactName">Selecione o tipo de resposta</label>
                    <Select
                      value={selectedAnswerStatus}
                      options={mappedAnswerStatusOptions}
                      onChange={(x) => handleSelectedAnswerStatus(x as ISelectedValue)}
                      placeholder="Selecione..."
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      classNamePrefix="react-select"
                      className="react-select-container"
                    />
                  </FormGroup>

                  <FormGroup aside={true}>
                    <label htmlFor="contactName">Selecione a categoria</label>
                    <Select
                      value={selectedAnswerType}
                      options={mappedAnswerTypesOptions}
                      onChange={(x) => handleSelectedAnswerType(x as ISelectedValue)}
                      placeholder="Selecione..."
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      classNamePrefix="react-select"
                      className="react-select-container"
                      isDisabled={!selectedAnswerStatus.value}
                    />
                  </FormGroup>
                </SelectsContainer>

                <div>
                  <Textarea
                    value={answerText}
                    onChange={handleCommentTextChange}
                    placeholder="O que você deseja responder?"
                    height={60}
                    maxLength={1000}
                  />

                  <Button width='96px' onClick={() => addAnswerOrReclamation()} disabled={!selectedAnswerStatus.value || !answerText || !canEnableActions}>
                    <img className='icon' src={whitePaperPlane} alt="send" title="Enviar" />
                  </Button>
                </div>
              </AddCommentContainer>
            </Section>
          )}

          {(!isMissingAnswer && isCustomer && reclamationsPermissions?.incluir) && (
            <Section>
              <SectionHeader>
                <div className="section-title">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                  </svg>
                  Nova Contestação
                </div>
              </SectionHeader>
              <AddCommentContainer>
                <div>
                  <Textarea
                    value={answerText}
                    onChange={handleCommentTextChange}
                    placeholder="Qual a sua solicitação de ajuste deste resultado?"
                    height={60}
                    maxLength={300}
                  />

                  <Button width='96px' onClick={() => addAnswerOrReclamation()} disabled={!answerText || !canEnableActions}>
                    <img className='icon' src={whitePaperPlane} alt="send" title="Enviar" />
                  </Button>
                </div>
              </AddCommentContainer>
            </Section>
          )}
        </ModalContainer>
      )}
      closeButtonLabel="Fechar"
      size="xl"
      type='info'
    />
  );
}
